import { Spinner } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import liveChatCRUDActions from '@mm/state/actions/LiveChatCRUDActions';
import { SelectedContact } from '@mm/state/interfaces/contacts';
import { LiveChatInboxStatus } from '@mm/state/model/LivechatConversationModel';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor } from '@xstate/react';
import blueCheckIcon from 'assets/icons/blue-check-circle.svg';
import clockIcon from 'assets/icons/clock.svg';
import closeIcon from 'assets/icons/close.svg';
import doneCheck from 'assets/icons/done-check.svg';
import moveToMain from 'assets/icons/main-box.svg';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useContextSelector } from 'use-context-selector';
import { useOneTimeConfirmationModal } from '../../../../../common/hooks/use-one-time-confirmation-modal';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { LiveChatContext } from '../../LiveChatContext';
import { Panes } from '../../LiveChatPanel';
import { ChannelIcon } from '../ChannelIcon';
import { ConversationAssignmentDropdown } from '../ConversationAssignmentDropdown/ConversationAssignmentDropdown';
import { OnlineIndicator } from '../OnlineIndicator/OnlineIndicator';
import './ConversationHeader.pcss';

export const ConversationHeader = ({
  setActivePane,
}: {
  setActivePane: (pane: Panes) => void;
}) => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const { isMobile } = useResolutionInfo();
  const dispatch: Dispatch<StoreState> = useDispatch();

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const { remoteUserId, remoteFbPageId, onlineRemoteUserIds } =
    lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const selectedConversation = selectedContact?.conversation;
  const assignedBy = selectedConversation?.userAssignedBy?.includes(
    'Automatic Unassignment',
  )
    ? selectedConversation?.userAssignedBy
    : selectedConversation?.userAssignedBy?.slice(
        0,
        selectedConversation?.userAssignedBy?.indexOf(' |'),
      );

  const selectedBy = useMemo(() => {
    return selectedConversation ? (
      selectedConversation.userAssignedBy &&
      selectedConversation.userAssignedAt ? (
        <div className="conversation-header__assigned-by">
          assigned by: <b>{assignedBy}</b>
          {` on ${moment(selectedConversation.userAssignedAt).format(
            'MM/DD/YYYY, h:mm:ss',
          )}`}
        </div>
      ) : selectedConversation.userAssignedBy ? (
        <div className="conversation-header__assigned-by">
          assigned by: <b>{assignedBy}</b>
        </div>
      ) : null
    ) : null;
  }, [JSON.stringify(selectedConversation)]);

  const confirmationModal = useOneTimeConfirmationModal(
    'inbox_linked_convo_status_update',
    {
      messageText:
        'Moving this conversation will also move all linked contacts',
      headerText: 'Moving Linked Contact',
      okButtonText: 'Move Conversations',
      cancelButtonFirst: true,
    },
  );

  if (!selectedContact) return null;

  const isOnline = onlineRemoteUserIds.has(selectedContact?.remoteId);
  const setStatus = async (inboxStatus: LiveChatInboxStatus) => {
    if (remoteFbPageId && remoteUserId) {
      const updateStatus = async () => {
        await dispatch(
          liveChatCRUDActions.updateSingleConversationStatus({
            remotePageId: remoteFbPageId,
            remoteUserIds: [remoteUserId],
            inboxStatus,
          }),
        );

        lcMachineSend({
          type: 'LC_CONVERSATIONS_REFRESH',
          payload: [remoteUserId],
        });

        lcMachineSend({
          type: 'LC_CONVERSATIONS_REFILTER',
          payload: [remoteUserId],
        });

        lcMachineSend({
          type: 'LC_REMOTE_USER_ID_CHANGE',
          payload: undefined,
        });
      };
      if (selectedContact?.linkedContacts?.length) {
        confirmationModal({ onConfirm: updateStatus });
      } else {
        updateStatus();
      }
    }
  };

  const generateName = (selectedContact: SelectedContact) => {
    if (
      selectedContact.firstName &&
      selectedContact.lastName &&
      selectedContact.username
    ) {
      if (
        selectedContact.firstName === 'Instagram Contact' &&
        selectedContact.lastName === 'IG' &&
        selectedContact.username
      ) {
        return `@${selectedContact.username}`;
      }
      return `${selectedContact.firstName} ${selectedContact.lastName}`;
    }
    if (selectedContact.firstName && selectedContact.lastName) {
      return `${selectedContact.firstName} ${selectedContact.lastName}`;
    }
    if (selectedContact.firstName) {
      return `${selectedContact.firstName}`;
    }
    if (selectedContact.lastName) {
      return `${selectedContact.lastName}`;
    }
    if (selectedContact.username) {
      return `@${selectedContact.username}`;
    }
    if (selectedContact.email) {
      return selectedContact.email;
    }
  };

  const renderMenuItem = (
    src: string,
    tooltip: string,
    status: LiveChatInboxStatus,
  ) => {
    if (selectedContact.conversation?.inboxStatus === status) {
      return;
    }
    return (
      <span
        className="conversation-header__move-to-outline"
        onClick={() => setStatus(status)}
      >
        <img
          src={src}
          className="conversation-header__move-to-icon"
          alt={tooltip}
        />
        <div className="conversation-header__move-to-status-tooltip">
          {tooltip}
        </div>
      </span>
    );
  };

  return (
    <div className="conversation-header">
      {isMobile && (
        <img
          src={closeIcon}
          alt="Return to conversation list"
          onClick={() => setActivePane(Panes.conversations)}
        />
      )}
      <ConversationAssignmentDropdown />
      {selectedBy}
      <div className="conversation-header__contact-info">
        <div className="conversation-header__channel-icon">
          <ChannelIcon remoteUserId={selectedContact.remoteId} />
        </div>
        <div className="conversation-header__contact-name">
          {generateName(selectedContact)}
        </div>
        {isOnline && <OnlineIndicator />}
      </div>
      <div className="conversation-header__move-to-wrapper">
        Move to
        {selectedContact.loading ? (
          <Spinner
            show
            size={14}
            position="relative"
            backgroundColor="transparent"
          />
        ) : (
          <>
            {selectedContact.conversation &&
              renderMenuItem(moveToMain, 'Move to Main', 'main')}
            {renderMenuItem(clockIcon, 'Move to Pending', 'pending')}
            {renderMenuItem(blueCheckIcon, 'Move to Resolved ', 'resolved')}
            {renderMenuItem(doneCheck, 'Move to Done', 'done')}
          </>
        )}
      </div>
    </div>
  );
};
